import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Show } from '@refinedev/antd'
import {
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Space,
  Avatar,
  Typography,
  Tag,
  Timeline,
  Spin,
  Result,
  Descriptions,
  message,
  Divider,
  Row,
  Col,
} from 'antd'
import {
  ArrowLeftOutlined,
  UserOutlined,
  PhoneOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  MedicineBoxOutlined,
  MailOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

const { Text } = Typography
const { TextArea } = Input

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  }
}

interface Doctor {
  id: string
  name: string
  specialty: string
  image: string | null
}

interface Patient {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  image: string | null
  dateOfBirth: string | null
  gender: string | null
  profile: {
    address: string | null
    city: string | null
    postalCode: string | null
  } | null
}

interface ConsultationDetail {
  id: string
  workflowInstanceId: string
  enteredAt: string
  dueAt: string | null
  isOverdue: boolean
  patient: Patient
  workflowInstance: {
    states: Array<{
      id: string
      status: string
      enteredAt: string
      completedAt: string | null
      stateDefinition: {
        code: string
        name: string
      }
    }>
  }
  availableDoctors: Doctor[]
  // Request data from patient
  serviceType?: string
  reason?: string
  preferredTimes?: string[]
  contactPreference?: 'PHONE' | 'EMAIL'
}

interface ScheduleFormValues {
  doctorId: string
  scheduledAt: dayjs.Dayjs
  duration: number
  notes?: string
}

export default function ConsultationShowPage() {
  const { id } = useParams<{ id: string }>()
  const [form] = Form.useForm<ScheduleFormValues>()

  const [data, setData] = useState<ConsultationDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [scheduledAppointment, setScheduledAppointment] = useState<{
    scheduledAt: string
    doctor: { name: string }
  } | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        console.log('[ConsultationShow] Fetching consultation:', id)
        const res = await fetch(`${API_URL}/admin/consultations/${id}`, {
          headers: getAuthHeaders(),
        })
        console.log('[ConsultationShow] Response status:', res.status)
        if (!res.ok) {
          const errorText = await res.text()
          console.error('[ConsultationShow] Error response:', errorText)
          throw new Error('Failed to fetch')
        }
        const json: ConsultationDetail = await res.json()
        console.log('[ConsultationShow] Data:', json)
        setData(json)
      } catch (error) {
        console.error('Error fetching consultation:', error)
        message.error('Greška pri učitavanju zahteva')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [id])

  const handleSubmit = async (values: ScheduleFormValues) => {
    setIsSubmitting(true)
    try {
      const scheduledAt = values.scheduledAt.toISOString()

      const res = await fetch(`${API_URL}/admin/consultations/${id}/schedule`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          doctorId: values.doctorId,
          scheduledAt,
          duration: values.duration,
          notes: values.notes || undefined,
        }),
      })

      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.message || 'Failed to schedule')
      }

      const result = await res.json()
      setScheduledAppointment({
        scheduledAt: result.appointment.scheduledAt,
        doctor: result.appointment.doctor,
      })
      setIsSuccess(true)
      message.success('Konsultacija uspešno zakazana!')
    } catch (error) {
      console.error('Error scheduling consultation:', error)
      message.error('Greška pri zakazivanju. Pokušajte ponovo.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Učitavanje..." />
      </div>
    )
  }

  if (!data) {
    return (
      <Result
        status="error"
        title="Greška"
        subTitle="Nije moguće učitati zahtev za konsultaciju"
        extra={
          <Link to="/consultations">
            <Button type="primary">Nazad na listu</Button>
          </Link>
        }
      />
    )
  }

  const patient = data.patient
  const patientName = patient?.firstName || patient?.lastName
    ? `${patient.firstName || ''} ${patient.lastName || ''}`.trim()
    : patient?.email || 'Nepoznat pacijent'

  // Success state
  if (isSuccess && scheduledAppointment) {
    const appointmentDate = new Date(scheduledAppointment.scheduledAt)
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '40px 0' }}>
        <Result
          status="success"
          title="Konsultacija zakazana!"
          subTitle={`Uspešno ste zakazali konsultaciju za pacijenta ${patientName}`}
          extra={[
            <Card key="details" size="small" style={{ textAlign: 'center', marginBottom: 16 }}>
              <Space size="large">
                <Space>
                  <CalendarOutlined style={{ color: '#1677ff' }} />
                  <Text>
                    {appointmentDate.toLocaleDateString('sr-Latn-RS', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </Text>
                </Space>
                <Space>
                  <ClockCircleOutlined style={{ color: '#1677ff' }} />
                  <Text>
                    {appointmentDate.toLocaleTimeString('sr-Latn-RS', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                </Space>
              </Space>
              <Divider />
              <Space>
                <MedicineBoxOutlined />
                <Text>{scheduledAppointment.doctor.name}</Text>
              </Space>
            </Card>,
            <Space key="buttons">
              <Link to="/consultations">
                <Button type="primary">Nazad na listu</Button>
              </Link>
              <Link to={`/patients/${patient?.id}`}>
                <Button>Profil pacijenta</Button>
              </Link>
            </Space>,
          ]}
        />
      </div>
    )
  }

  return (
    <Show
      title="Zakaži konsultaciju"
      headerButtons={() => (
        data.isOverdue ? <Tag color="error">SLA istekao</Tag> : null
      )}
      goBack={
        <Link to="/consultations">
          <Button icon={<ArrowLeftOutlined />} type="text">
            Nazad
          </Button>
        </Link>
      }
    >
      <Row gutter={24}>
        {/* Patient Info */}
        <Col xs={24} lg={8}>
          <Card title="Pacijent" size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Avatar
                  size={48}
                  src={patient?.image}
                  icon={!patient?.image && <UserOutlined />}
                  style={{ backgroundColor: '#1677ff' }}
                />
                <div>
                  <Text strong>{patientName}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {patient?.email}
                  </Text>
                </div>
              </Space>

              <Divider style={{ margin: '12px 0' }} />

              <Descriptions column={1} size="small">
                {patient?.phone && (
                  <Descriptions.Item label={<PhoneOutlined />}>
                    <a href={`tel:${patient.phone}`}>{patient.phone}</a>
                  </Descriptions.Item>
                )}
                {patient?.dateOfBirth && (
                  <Descriptions.Item label="Rođen/a">
                    {new Date(patient.dateOfBirth).toLocaleDateString('sr-Latn-RS')}
                  </Descriptions.Item>
                )}
                {patient?.profile?.city && (
                  <Descriptions.Item label="Grad">
                    {patient.profile.city}
                  </Descriptions.Item>
                )}
              </Descriptions>

              <Link to={`/patients/${patient?.id}`}>
                <Button block>Pogledaj profil</Button>
              </Link>
            </Space>
          </Card>

          {/* Request Info */}
          {(data.reason || data.preferredTimes || data.serviceType) && (
            <Card
              title={
                <Space>
                  <InfoCircleOutlined />
                  Zahtev pacijenta
                </Space>
              }
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {data.serviceType && (
                  <div>
                    <Text type="secondary">Usluga:</Text>
                    <Tag
                      color={data.serviceType === 'trt' ? 'green' : 'blue'}
                      style={{ marginLeft: 8 }}
                    >
                      {data.serviceType === 'trt' ? 'TRT' :
                       data.serviceType === 'general' ? 'Opšte konsultacije' :
                       data.serviceType === 'dermatology' ? 'Dermatologija' :
                       data.serviceType === 'menopause' ? 'Menopauza' :
                       data.serviceType}
                    </Tag>
                  </div>
                )}

                {data.contactPreference && (
                  <div>
                    <Text type="secondary">Kontakt:</Text>
                    <Space style={{ marginLeft: 8 }}>
                      {data.contactPreference === 'EMAIL' ? (
                        <>
                          <MailOutlined />
                          <Text>Email</Text>
                        </>
                      ) : (
                        <>
                          <PhoneOutlined />
                          <Text>Telefon</Text>
                        </>
                      )}
                    </Space>
                  </div>
                )}

                {data.preferredTimes && data.preferredTimes.length > 0 && (
                  <div>
                    <Text type="secondary">Željeni termini:</Text>
                    <div style={{ marginTop: 4 }}>
                      {data.preferredTimes.map((time) => (
                        <Tag key={time} style={{ marginBottom: 4 }}>
                          {time === 'morning' ? 'Jutro (9-12h)' :
                           time === 'afternoon' ? 'Popodne (12-17h)' :
                           time === 'evening' ? 'Veče (17-20h)' :
                           time}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                {data.reason && (
                  <div>
                    <Text type="secondary">Razlog:</Text>
                    <div
                      style={{
                        marginTop: 4,
                        padding: 8,
                        backgroundColor: '#fafafa',
                        borderRadius: 4,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <Text>{data.reason}</Text>
                    </div>
                  </div>
                )}
              </Space>
            </Card>
          )}

          {/* Workflow History */}
          <Card title="Istorija" size="small">
            <Timeline
              items={data.workflowInstance.states
                .filter((s) => s.status === 'COMPLETED' || s.stateDefinition.code === 'TRT_CONSULTATION_REQUESTED')
                .slice(-5)
                .map((state) => ({
                  color: state.status === 'COMPLETED' ? 'green' : 'orange',
                  children: (
                    <div>
                      <Text>{state.stateDefinition.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {new Date(state.enteredAt).toLocaleDateString('sr-Latn-RS')}
                      </Text>
                    </div>
                  ),
                }))}
            />
          </Card>
        </Col>

        {/* Scheduling Form */}
        <Col xs={24} lg={16}>
          <Card title="Detalji termina">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                duration: 30,
                scheduledAt: dayjs().add(1, 'hour').startOf('hour'),
              }}
            >
              <Form.Item
                name="doctorId"
                label="Izaberite doktora"
                rules={[{ required: true, message: 'Izaberite doktora' }]}
              >
                <Select
                  placeholder="-- Izaberite doktora --"
                  size="large"
                  options={data.availableDoctors.map((doctor) => ({
                    value: doctor.id,
                    label: (
                      <Space>
                        <Avatar size="small" icon={<MedicineBoxOutlined />} />
                        {doctor.name} - {doctor.specialty}
                      </Space>
                    ),
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="scheduledAt"
                label="Datum i vreme"
                rules={[{ required: true, message: 'Izaberite datum i vreme' }]}
              >
                <DatePicker
                  showTime={{ format: 'HH:mm', minuteStep: 15 }}
                  format="DD.MM.YYYY HH:mm"
                  size="large"
                  style={{ width: '100%' }}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>

              <Form.Item
                name="duration"
                label="Trajanje (minuta)"
                extra="Standardna konsultacija traje 30 minuta"
              >
                <Select
                  size="large"
                  options={[
                    { value: 15, label: '15 minuta' },
                    { value: 30, label: '30 minuta' },
                    { value: 45, label: '45 minuta' },
                    { value: 60, label: '60 minuta' },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="notes"
                label="Beleške (opciono)"
              >
                <TextArea
                  placeholder="Dodatne napomene za pacijenta ili doktora..."
                  rows={3}
                />
              </Form.Item>

              <Divider />

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Link to="/consultations">
                    <Button>Odustani</Button>
                  </Link>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    icon={<CalendarOutlined />}
                  >
                    Zakaži konsultaciju
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </Show>
  )
}
